import React from "react";
import { SubscriptionPlan } from "./models";

type PlanCardsProps = {
  plans: SubscriptionPlan[];
  currentPlanType: string | null;
  onSubscribe: (planType: string) => void;
  onUpgrade: (planType: string) => void;
  subscribeLoading: boolean;
  upgradeLoading: boolean;
};

const planOrder = ["basic", "premium", "pro"];
const planColors: Record<string, string> = {
  basic: "#48BB78",
  premium: "#FFBB28",
  pro: "#FF6028",
};

const planFeatures: Record<string, string[]> = {
  basic: [
    "1 sucursal",
    "2 usuarios",
    "500 ordenes/mes",
    "Soporte por email",
  ],
  premium: [
    "2 sucursales",
    "20 usuarios",
    "1,500 ordenes/mes",
    "Soporte prioritario",
    "Reportes avanzados",
  ],
  pro: [
    "4 sucursales",
    "Usuarios ilimitados",
    "5,000 ordenes/mes",
    "Soporte dedicado",
    "Reportes avanzados",
    "API access",
  ],
};

const PlanCards: React.FC<PlanCardsProps> = ({
  plans,
  currentPlanType,
  onSubscribe,
  onUpgrade,
  subscribeLoading,
  upgradeLoading,
}) => {
  const sortedPlans = [...plans].sort(
    (a, b) => planOrder.indexOf(a.key) - planOrder.indexOf(b.key),
  );

  const getButtonConfig = (planKey: string) => {
    if (!currentPlanType || currentPlanType === "free") {
      return {
        text: "Contratar",
        onClick: () => onSubscribe(planKey),
        disabled: subscribeLoading,
        variant: "btn-primary",
      };
    }

    if (currentPlanType === planKey) {
      return {
        text: "Plan Actual",
        onClick: () => {},
        disabled: true,
        variant: "btn-secondary",
      };
    }

    const currentIndex = planOrder.indexOf(currentPlanType);
    const targetIndex = planOrder.indexOf(planKey);

    if (targetIndex > currentIndex) {
      return {
        text: "Cambiar Plan",
        onClick: () => onUpgrade(planKey),
        disabled: upgradeLoading,
        variant: "btn-warning",
      };
    }

    return {
      text: "Contactar Soporte",
      onClick: () => {
        window.open("mailto:soporte@fooapp.mx?subject=Cambio de plan", "_blank");
      },
      disabled: false,
      variant: "btn-outline-secondary",
    };
  };

  return (
    <div className="row">
      {sortedPlans.map((plan) => {
        const btn = getButtonConfig(plan.key);
        const isCurrentPlan = currentPlanType === plan.key;
        const color = planColors[plan.key] || "#FFBB28";
        const features = planFeatures[plan.key] || [];

        return (
          <div className="col-md-4 mb-4" key={plan.key}>
            <div
              className="card h-100"
              style={{
                borderTop: `4px solid ${color}`,
                boxShadow: isCurrentPlan
                  ? `0 0 0 2px ${color}`
                  : "0 1px 3px rgba(0,0,0,0.1)",
              }}
            >
              <div className="card-body d-flex flex-column">
                {isCurrentPlan && (
                  <span
                    className="badge mb-2"
                    style={{
                      backgroundColor: color,
                      color: "#fff",
                      alignSelf: "flex-start",
                    }}
                  >
                    Plan Actual
                  </span>
                )}
                <h4 className="card-title">{plan.name}</h4>
                <div className="mb-3">
                  <span style={{ fontSize: "2rem", fontWeight: "bold" }}>
                    ${plan.priceYearly.toLocaleString("es-MX")}
                  </span>
                  <span className="text-muted"> /año</span>
                </div>
                <ul className="list-unstyled mb-4 flex-grow-1">
                  {features.map((feature, i) => (
                    <li key={i} className="mb-2">
                      <i
                        className="fas fa-check-circle mr-2"
                        style={{ color }}
                      />{" "}
                      {feature}
                    </li>
                  ))}
                </ul>
                <button
                  className={`btn ${btn.variant} btn-block`}
                  onClick={btn.onClick}
                  disabled={btn.disabled}
                >
                  {(subscribeLoading || upgradeLoading) && !btn.disabled
                    ? ""
                    : ""}
                  {btn.text}
                </button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default PlanCards;
